import React, { useState, useEffect } from 'react';
import {
    Camera, Upload, X, ChevronLeft, ChevronRight,
    Trash2, Maximize2, Calendar, LayoutGrid, User, Loader2
} from 'lucide-react';
import api from '../api/axiosInstance';

const ProjectGallery = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [images, setImages] = useState([]);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Slideshow States
    const [currentIndex, setCurrentIndex] = useState(null);
    const [uploadData, setUploadData] = useState({ photos: [], caption: '', task_id: '' });
    const [tasks, setTasks] = useState([]);

    useEffect(() => {
        api.get('get_projects.php').then(res => setProjects(res.data.projects));
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchGallery();
            api.get(`get_tasks.php?project_id=${selectedProject}`).then(res => setTasks(res.data.tasks));
        }
    }, [selectedProject]);

    const fetchGallery = async () => {
        const res = await api.get(`get_gallery.php?project_id=${selectedProject}`);
        setImages(res.data.images || []);
    };

    const handleDelete = async (e, imageId) => {
        e.stopPropagation();
        if (!window.confirm("Delete this site photo? This cannot be undone.")) return;

        const res = await api.post('delete_gallery_image.php', { id: imageId });
        if (res.data.status === 'success') {
            const nextIndex = currentIndex >= images.length - 1 ? null : currentIndex;
            setCurrentIndex(nextIndex);
            fetchGallery();
        }
    };

    const nextSlide = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevSlide = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setUploadData({ ...uploadData, photos: selectedFiles });
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (uploadData.photos.length === 0) return alert("Please select at least one photo");

        setIsUploading(true);
        const formData = new FormData();

        // Append multiple files with the name photos[]
        uploadData.photos.forEach((file) => {
            formData.append('photos[]', file);
        });

        formData.append('project_id', selectedProject);
        formData.append('task_id', uploadData.task_id);
        formData.append('caption', uploadData.caption);

        try {
            const res = await api.post('upload_gallery.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status === 'success') {
                setIsUploadOpen(false);
                setUploadData({ photos: [], caption: '', task_id: '' });
                fetchGallery();
            }
        } catch (error) {
            console.error("Upload failed", error);
            alert("Upload failed. Please check file sizes.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">Project Gallery</h1>
                    <select
                        className="mt-2 bg-transparent border-none font-bold text-indigo-600 outline-none text-lg cursor-pointer"
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value)}
                    >
                        <option value="">Select Project Site...</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                    </select>
                </div>
                {selectedProject && (
                    <button onClick={() => setIsUploadOpen(true)} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all">
                        <Camera size={20} /> Add Site Photos
                    </button>
                )}
            </div>

            {/* MASONRY GRID */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {images.map((img, index) => (
                    <div
                        key={img.id}
                        className="group relative aspect-square rounded-[32px] overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-xl transition-all"
                        onClick={() => setCurrentIndex(index)}
                    >
                        <img
                            src={`https://backendibos.aartiassociates.com/${img.image_path}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            alt={img.caption}
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Maximize2 className="text-white" size={32} />
                        </div>
                    </div>
                ))}
            </div>

            {/* SLIDESHOW MODAL */}
            {currentIndex !== null && images[currentIndex] && (
                <div className="fixed inset-0 z-[300] bg-slate-900/95 backdrop-blur-xl flex items-center justify-center select-none" onClick={() => setCurrentIndex(null)}>
                    <button onClick={prevSlide} className="absolute left-8 p-4 text-white hover:bg-white/10 rounded-full transition-all"><ChevronLeft size={48} /></button>
                    <button onClick={nextSlide} className="absolute right-8 p-4 text-white hover:bg-white/10 rounded-full transition-all"><ChevronRight size={48} /></button>

                    <div className="absolute top-8 right-8 flex gap-4">
                        <button
                            onClick={(e) => handleDelete(e, images[currentIndex].id)}
                            className="p-4 bg-rose-500/20 text-rose-500 hover:bg-rose-500 hover:text-white rounded-2xl transition-all"
                        >
                            <Trash2 size={24} />
                        </button>
                        <button onClick={() => setCurrentIndex(null)} className="p-4 bg-white/10 text-white hover:bg-white/20 rounded-2xl transition-all"><X size={24} /></button>
                    </div>

                    <div className="max-w-5xl max-h-[80vh] flex flex-col items-center gap-6" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={`https://backendibos.aartiassociates.com/${images[currentIndex].image_path}`}
                            className="max-w-full max-h-[70vh] rounded-[40px] shadow-2xl object-contain border border-white/10"
                            alt="Site View"
                        />
                        <div className="text-center px-10">
                            <h3 className="text-white text-2xl font-black italic tracking-tight">{images[currentIndex].caption || 'No Caption'}</h3>
                            <div className="flex items-center justify-center gap-6 mt-4 text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                                <span className="flex items-center gap-2"><User size={12} /> {images[currentIndex].uploaded_by}</span>
                                <span className="flex items-center gap-2"><Calendar size={12} /> {images[currentIndex].created_at}</span>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-8 flex gap-2 overflow-x-auto max-w-2xl p-2 px-10 no-scrollbar">
                        {images.map((thumb, idx) => (
                            <img
                                key={thumb.id}
                                src={`https://backendibos.aartiassociates.com/${thumb.image_path}`}
                                className={`h-12 w-12 object-cover rounded-xl cursor-pointer transition-all ${idx === currentIndex ? 'ring-2 ring-indigo-500 scale-110' : 'opacity-40'}`}
                                onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* UPLOAD MODAL */}
            {isUploadOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md">
                    <form onSubmit={handleUpload} className="bg-white w-full max-w-xl rounded-[48px] shadow-2xl p-12 space-y-6 relative overflow-hidden">

                        {/* Spinner Overlay */}
                        {isUploading && (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                                <p className="font-black italic text-slate-900 uppercase tracking-widest text-sm">Syncing to Cloud...</p>
                            </div>
                        )}

                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-black italic uppercase tracking-tighter">Bulk Upload Site Photos</h2>
                            <button type="button" onClick={() => setIsUploadOpen(false)} disabled={isUploading}><X /></button>
                        </div>

                        <div className="relative aspect-video border-2 border-dashed border-slate-200 rounded-[32px] flex flex-col items-center justify-center overflow-hidden hover:border-indigo-500 transition-all bg-slate-50">
                            {uploadData.photos.length > 0 ? (
                                <div className="flex flex-wrap gap-2 p-4 justify-center">
                                    {uploadData.photos.map((file, i) => (
                                        <div key={i} className="w-16 h-16 rounded-xl overflow-hidden bg-slate-200">
                                            <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                    <div className="w-full text-center mt-2 font-black text-indigo-600 text-xs uppercase">
                                        {uploadData.photos.length} Files Selected
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Upload size={40} className="text-slate-300 mb-2" />
                                    <span className="text-xs font-bold text-slate-400">Click to select multiple photos</span>
                                </>
                            )}
                            <input
                                required
                                type="file"
                                accept="image/*"
                                multiple // ALLOW MULTIPLE FILES
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                disabled={isUploading}
                                onChange={handleFileChange}
                            />
                        </div>

                        <select
                            className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                            disabled={isUploading}
                            onChange={e => setUploadData({ ...uploadData, task_id: e.target.value })}
                        >
                            <option value="">Link to Milestone (Optional)</option>
                            {tasks.map(t => <option key={t.id} value={t.id}>{t.task_name}</option>)}
                        </select>

                        <textarea
                            placeholder="Description for this batch..."
                            className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm h-24 outline-none"
                            disabled={isUploading}
                            onChange={e => setUploadData({ ...uploadData, caption: e.target.value })}
                        />

                        <button
                            disabled={isUploading}
                            className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black shadow-xl uppercase italic hover:bg-indigo-700 transition-all"
                        >
                            {isUploading ? 'Uploading...' : 'Post to Gallery'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ProjectGallery;